import type { wave } from '@round/api';

const LIVE_CONTRACT_STATUSES: wave.ContractStatus[] = ['accepted', 'proposed'];
const ACCEPTABLE_AGAIN_STATUSES: wave.ContractStatus[] = ['declined', 'expired'];

type PickableContract = Pick<wave.Contract, 'id' | 'status' | 'created'>;

const isNewer = (a: PickableContract, b: PickableContract): boolean => {
    const createdDiff = new Date(a.created).getTime() - new Date(b.created).getTime();
    return createdDiff === 0 ? a.id > b.id : createdDiff > 0;
};

const getNewest = <T extends PickableContract>(contracts: T[]): T | undefined =>
    contracts.reduce<T | undefined>(
        (newest, contract) => (!newest || isNewer(contract, newest) ? contract : newest),
        undefined
    );

// A contract accept narrows the contract's accounts but not the invite's, so the invite's
// accounts (which carry avatars and profile links) are filtered to the contract's.
export const getContractAccounts = <T extends { id: number }>(
    accounts: T[],
    contract: Pick<wave.Contract, 'allowed_accounts'> | undefined
): T[] => {
    if (!contract) {
        return accounts;
    }
    const contractAccountIds = new Set(contract.allowed_accounts.map((account) => account.id));
    return accounts.filter((account) => contractAccountIds.has(account.id));
};

// A decline then accept, or a new round, leaves closed contracts beside the live one. The
// newest live contract is the one to answer and post against; with none live, the newest
// declined or expired one can still be accepted again. A fulfilled or cancelled contract
// can't be answered, so it's never picked.
export const getInviteContract = <T extends PickableContract>(contracts: T[] | undefined): T | undefined => {
    if (!contracts) {
        return undefined;
    }
    return (
        getNewest(contracts.filter((contract) => LIVE_CONTRACT_STATUSES.includes(contract.status))) ??
        getNewest(contracts.filter((contract) => ACCEPTABLE_AGAIN_STATUSES.includes(contract.status)))
    );
};

// An accept leaves the post count out until creators choose one.
export const getRespondToInviteBody = (
    contract: Pick<wave.Contract, 'id'>,
    response: wave.InviteResponseChoice,
    reason: string,
    creatorAccounts?: number[]
): wave.RespondToInviteBody => {
    if (response === 'declined') {
        return { response, contract_id: contract.id, reason: reason || undefined };
    }
    return { response, contract_id: contract.id, creator_accounts: creatorAccounts };
};
